import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserLessons, getUserAttendance, getUserLessonsHistory, getUserTicketOnly } from '../../../api/user';
import { attendMembersToClassroomDelete } from '../../../api/ticket';
import { useTranslation } from 'react-i18next';
import { errorMessages, exportStyledExcel } from '../../../utils/methods';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';
import { formatDate } from '../../../utils/methods';
import CommonLoading from '../../../form-components/CommonLoading';
import CommonPagination from '../../../form-components/CommonPagination';
import CommonSelect from '../../../form-components/CommonSelect';
import ConfirmModal from '../../common/modal/ConfirmModal';
import { CommonExcelButton } from '../../../form-components/CommonExcelButton';
import ReservationEventTimeline, { ReservationHistoryToggle, formatActor } from '../common/ReservationEventTimeline';

const initialQuery = {
  page: 1,
  limit: 20,
};

// 엑셀은 보고 있는 페이지가 아니라 현재 필터 전체를 담는다.
// 전용 엔드포인트를 만들지 않고 같은 조회 API 를 큰 limit 으로 한 번 더 부른다.
const EXCEL_FETCH_LIMIT = 5000;

// 수강권 상태 코드 → 한국어 라벨.
const TICKET_STATUS_LABELS = {
  ACTIVE: '사용',
  INACTIVE: '만료',
  STOPPED: '중지',
  TRANSFERRED: '양도',
  REFUNDED: '환불',
};

// 레슨 출석/예약 상태 select 옵션.
const LESSON_STATUS_OPTIONS = [
  { value: 'ATTENDED', label: '출석' },
  { value: 'ABSENT', label: '결석' },
  { value: 'RESERVED', label: '예약' },
  { value: 'CANCELED', label: '취소' },
];

// 예약 취소 주체 코드 → 한국어 라벨.
const CANCEL_ACTOR_LABELS = {
  MEMBER: '회원',
  ADMIN: 'CRM',
  TRAINER: '강사',
  SYSTEM: '시스템',
};

// 취소자 표기. 시스템 폐강은 취소자가 없고, 컬럼 도입 이전 데이터는
// 직원 취소건의 주체(CRM/강사) 구분이 불가능해 이름만 나온다.
const formatCanceledBy = lesson => {
  if (!lesson.canceledDateTime) return '-';
  const actor = CANCEL_ACTOR_LABELS[lesson.canceledByType];
  if (lesson.canceledByType === 'SYSTEM') return '시스템(폐강)';
  if (!lesson.canceledByName) return actor ?? '-';
  return actor ? `${lesson.canceledByName}(${actor})` : lesson.canceledByName;
};

// 그룹레슨만 그룹 스케줄(/group)로 이동 가능. 개인/듀엣레슨은 예약 취소 시
// 수업 회차가 삭제되므로 이동할 대상이 없다.
// 다지점 공유 회원권은 타 지점 수업이 섞여 나오는데 /group 은 지점 단위로 조회하므로
// 이동해봐야 그 수업을 못 찾는다(빈 화면). 예약 내역 모달과 같은 규칙으로 막는다.
const isOtherBranchLesson = (lesson, currentBranchId) =>
  !!lesson.branchId && !!currentBranchId && lesson.branchId !== currentBranchId;

const canOpenGroupSchedule = (lesson, currentBranchId) =>
  lesson.courseClassType === 'GROUP_LESSON' && !!lesson.courseClassTimeId && !!lesson.classDateTime
  && !isOtherBranchLesson(lesson, currentBranchId);

// 다지점 공유 회원권이면 타 지점에서 열린 수업이 섞여 나온다. 그 표시용 뱃지.
// 개인레슨 탭은 클래스명 컬럼이 없어져서 이 뱃지를 레슨 일시 셀에 붙인다.
const renderBranchBadge = (lesson, currentBranchId) =>
  isOtherBranchLesson(lesson, currentBranchId) ? (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        marginRight: '4px',
        padding: '0 5px',
        borderRadius: '10px',
        border: '1px solid #E0E4EA',
        backgroundColor: '#F5F7FA',
        color: '#5C6470',
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: '16px',
        verticalAlign: 'middle',
      }}>
      {lesson.branchName}
    </Box>
  ) : null;

// 결석 행은 눈에 띄게 칠한다. 개인레슨·그룹레슨 탭 공통이며
// 입장내역 관리의 만료임박 행과 같은 분홍(#FFECEB) 을 쓴다.
const ABSENT_ROW_BG = '#FFECEB';
const isAbsent = lesson => lesson.status === 'ABSENT';

// 개인레슨 탭 셀 공통 스타일. 기존 탭은 인라인 sx 를 그대로 둔다.
const headCellSx = { borderRight: '1px solid #ddd', fontSize: '14px', fontWeight: 550 };
const bodyCellSx = { borderRight: '1px solid #ddd', fontSize: '13px' };

// 잔여 횟수는 서버가 그 수업 차감 후 잔여로 행마다 역산해 준다(예약중 행은 예정 차감까지 반영).
// 취소·대기 행과 무제한 수강권(총 횟수 없음)은 null 이라 '-' 로 그린다.
const formatRemainingCount = lesson =>
  lesson.remainingCount === null || lesson.remainingCount === undefined
    ? '-'
    : `${lesson.remainingCount}회`;

// 차감은 출결이 확정될 때 일어난다. 예약 행은 확정되면 빠질 몫을 미리 보여준다.
// (수강권 상세의 예약 내역 모달과 같은 규칙)
const renderDeductionCount = lesson => {
  if (lesson.deductionCount) return `${lesson.deductionCount}회`;
  if (lesson.scheduledDeductionCount) {
    return (
      <Box component="span" sx={{ color: '#8A93A0' }}>
        {`${lesson.scheduledDeductionCount}회 예정`}
      </Box>
    );
  }
  return '-';
};

// 개인레슨만 수업 1:예약 1 이라 수업 단위로 저장된 사인이 회원 한 명의 사인과 일치한다.
// 그룹레슨은 한 수업을 여러 회원이 공유하므로 사인 컬럼을 붙이지 않는다.
const isPrivateLessonTab = type => type === 'PRIVATE_LESSON' || type === 'OT';

// 사용 기간 단위(MONTH/DAY) → 한국어 표기.
const formatTicketLabel = ticket => {
  const periodText = ticket.usagePeriodType === 'MONTH' ? '개월' : '일';
  const statusText = TICKET_STATUS_LABELS[ticket.status] ?? ticket.status;
  return `${ticket.ticketName} ${ticket.usageCount}회 ${ticket.usagePeriod}${periodText} (${statusText})`;
};

export default function UserLessons({ user, bigUser, title, type, active }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // 정렬 상태 — 기본은 레슨일시 내림차순(백엔드 기본 동작과 동일).
  const [sortBy, setSortBy] = useState('CLASS_DATE');
  const [sortDirection, setSortDirection] = useState('DESC');
  // [이력] 펼친 예약 하나 — 다른 행을 펼치면 접힌다
  const [expandedId, setExpandedId] = useState(null);
  // 펼친 이력 행이 표 전체 폭을 차지하도록 헤더 칸 수를 센다(탭·사인 노출에 따라 컬럼 수가 다르다)
  const headRowRef = useRef(null);
  const [query, setQuery] = useState({
    ...initialQuery,
    branchId: bigUser.branch.branchId,
    courseClassType: type,
    sortBy: 'CLASS_DATE',
    sortDirection: 'DESC',
  });
  const [lessons, setLessons] = useState();
  const [ticketFilter, setTicketFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userTickets, setUserTickets] = useState([]);
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    lesson: null,
  });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  // 사인 확대 모달 — 공용 이미지 뷰어가 없어 이 화면 안에서만 쓴다.
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  // 개인레슨 탭은 클래스명 대신 사인 컬럼을 쓴다.
  const isPrivate = isPrivateLessonTab(type);
  // 사인은 수업 단위로 저장돼 여러 명이 함께 하는 OT 에서는 여러 회원 행에 같은 사인이 반복된다. OT 탭은 사인 컬럼을 숨긴다.
  const showSignature = type !== 'OT';

  const handleChangePage = (event, value) => {
    setQuery({ ...query, page: value });
  };

  // 엑셀 다운로드 — 현재 필터 그대로, 페이지만 전체로 바꿔 다시 조회한다.
  const handleExcelDownload = async () => {
    setIsExcelLoading(true);
    try {
      const excelQuery = QueryString.stringify({ ...query, page: 1, limit: EXCEL_FETCH_LIMIT });
      const data = await getUserLessonsHistory(user.memberId, excelQuery, bigUser.token);
      const rows = data?.content ?? [];
      if (rows.length === 0) {
        toast.error('다운로드할 내역이 없습니다');
        return;
      }
      const memberName = user?.fullname || user?.name || '';
      await exportStyledExcel(
        rows.map(lesson => ({
          '레슨 일시': formatDate(lesson.classDateTime, 'YYYY.MM.DD (dd) A hh:mm'),
          담당강사: lesson.representativeTrainer || '',
          출결: t(lesson.status),
          ...(showSignature ? { '회원 사인': lesson.signatureImage || '' } : {}),
          수강권명: lesson.courseTicketName || '',
          '잔여 횟수': lesson.remainingCount ?? '',
          '차감 횟수': lesson.deductionCount ?? lesson.scheduledDeductionCount ?? '',
        })),
        {
          filename: `${memberName}_개인레슨_출결예약`,
          sheetName: '개인레슨',
          // 센트리얼 사인지처럼 흰 바탕·얇은 검정 선·연회색 머리글.
          variant: 'form',
          imageColumns: showSignature ? ['회원 사인'] : [],
          // 회원은 행마다 반복되는 값이라 컬럼 대신 표 위 정보 블록으로 뺀다(사인지 양식과 같은 모양).
          infoRows: [
            [
              { label: '회원명', value: memberName },
              { label: '전화번호', value: user?.phoneNumber || '-' },
            ],
          ],
          // 출결만 글자색으로 구분한다 — 결석 빨강, 출석 초록. 배경은 칠하지 않는다.
          rowFontColor: row => {
            if (row.출결 === t('ABSENT')) return 'C62828';
            if (row.출결 === t('ATTENDED')) return '2E7D32';
            return null;
          },
          columnWidths: {
            '레슨 일시': 22,
            담당강사: 10,
            출결: 7,
            ...(showSignature ? { '회원 사인': 14 } : {}),
            수강권명: 18,
            '잔여 횟수': 9.5,
            '차감 횟수': 9.5,
          },
        }
      );
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setIsExcelLoading(false);
    }
  };

  // 예약일시(RESERVED_DATE)·레슨일시(CLASS_DATE) 컬럼 정렬 토글.
  const handleSortClick = field => {
    const isAsc = sortBy === field && sortDirection === 'ASC';
    const newDirection = isAsc ? 'DESC' : 'ASC';
    setSortBy(field);
    setSortDirection(newDirection);
    setQuery({ ...query, sortBy: field, sortDirection: newDirection, page: 1 });
  };

  const handleTicketFilterChange = event => {
    setTicketFilter(event.target.value);
    // API 호출 시 필터 적용하고 싶다면 query 상태에 포함
    setQuery({ ...query, memberCourseTicketId: event.target.value, page: 1 });
  };

  const handleStatusFilterChange = event => {
    setStatusFilter(event.target.value);
    // API 호출 시 필터 적용하고 싶다면 query 상태에 포함
    setQuery({ ...query, status: event.target.value, page: 1 });
  };

  // 예약 취소 기능
  const cancelLessonReservation = async lesson => {
    try {
      const body = {
        memberReservationIDs: [lesson.reservationId],
      };

      await attendMembersToClassroomDelete(lesson.courseClassTimeId, body, bigUser.token);

      // 성공 후 데이터 다시 로드
      getLessons();
      toast.success('예약이 취소되었습니다');
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 취소 다이얼로그 열기
  const handleCancelClick = lesson => {
    setCancelDialog({
      open: true,
      lesson: lesson,
    });
  };

  // 취소 다이얼로그 닫기
  const handleCancelDialogClose = () => {
    setCancelDialog({
      open: false,
      lesson: null,
    });
  };

  // 취소 확인 처리
  const handleCancelConfirm = () => {
    if (cancelDialog.lesson) {
      cancelLessonReservation(cancelDialog.lesson);
    }
    handleCancelDialogClose();
  };

  // 클래스명 클릭 → 그룹 스케줄에서 해당 수업으로 이동.
  // GroupLesson이 focusDate로 주간을 맞추고 focusClassTimeId로 스크롤·하이라이트한다.
  const handleClassNameClick = lesson => {
    if (!canOpenGroupSchedule(lesson, bigUser?.branch?.branchId)) return;
    const focusDate = formatDate(lesson.classDateTime, 'YYYY-MM-DD');
    navigate(`/group?focusClassTimeId=${lesson.courseClassTimeId}&focusDate=${focusDate}`);
  };

  // 액션 버튼 클릭 핸들러
  const handleActionClick = lesson => {
    // 상태별 처리
    if (lesson.status === 'CANCELED') {
      // 취소된 레슨은 아무 동작 안함 (disabled)
      return;
    } else {
      // 예약된 레슨은 취소 다이얼로그 표시
      handleCancelClick(lesson);
    }
  };

  const getLessons = async () => {
    const queryString = QueryString.stringify(query);
    try {
      //변경 -> 개인레슨과 그룹레슨조회
      const data = await getUserLessonsHistory(user.memberId, queryString, bigUser.token);
      setLessons(data);
      // 기존 탭에 따라 출결 / 예약 나눔
      // if (active) {
      //   const data = await getUserLessons(user.memberId, queryString, bigUser.token);
      //   setLessons(data);
      // } else {
      //   const data = await getUserAttendance(user.memberId, queryString, bigUser.token);
      //   setLessons(data);
      // }
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setIsInitialLoading(false);
    }
  };

  const getUserTickets = async () => {
    try {
      const queryString = QueryString.stringify(query);
      const data = await getUserTicketOnly(user.memberId, bigUser.token, queryString);
      setUserTickets(data || []);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  useEffect(() => {
    getLessons();
  }, [query, active]);

  useEffect(() => {
    if (user?.memberId && bigUser?.token) {
      getUserTickets();
    }
  }, [user?.memberId, bigUser?.token]);

  // 첫 진입 시 빈 테이블이 잠깐 보이는 것을 막는다.
  if (isInitialLoading) {
    return <CommonLoading minHeight={420} />;
  }

  return (
    <>
      <Typography variant="h6" className="title" sx={{ mt: '30px', mb: '10px' }}></Typography>

      {/* 필터 셀렉트박스 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          mb: 2,
        }}>
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, minWidth: '45px' }}>수강권</Typography>
          <CommonSelect
            value={ticketFilter}
            onChange={handleTicketFilterChange}
            emptyLabel="전체"
            options={userTickets.map(ticket => ({
              value: ticket.memberCourseTicketId,
              label: formatTicketLabel(ticket),
            }))}
          />
        </Stack>

        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, minWidth: '30px' }}>상태</Typography>
          <CommonSelect
            value={statusFilter}
            onChange={handleStatusFilterChange}
            emptyLabel="전체"
            width={120}
            options={LESSON_STATUS_OPTIONS}
          />
        </Stack>

        {/* 현재 조회 조건의 전체 건수 — 헤더 프로필 아바타 중심선과 가로 위치를 맞춤
            (헤더 우측 패딩 32px + 아바타 반지름 20px - 본문 우측 패딩 20px = 32px 안쪽으로) */}
        <Typography sx={{ ml: 'auto', fontSize: '14px', fontWeight: 600, color: '#555' }}>
          총 {(lessons?.totalElements ?? 0).toLocaleString()}건
        </Typography>

        {isPrivate && (
          <CommonExcelButton
            onClick={handleExcelDownload}
            disabled={isExcelLoading || !lessons?.totalElements}
            sx={{ mr: '32px', whiteSpace: 'nowrap' }}>
            {isExcelLoading ? t('다운로드 중...') : t('전체 엑셀 다운로드')}
          </CommonExcelButton>
        )}
      </Box>

      <TableContainer
        sx={{
          border: '1px solid #ddd',
          borderRadius: '5px',
        }}>
        <Table
          sx={{
            minWidth: 1360, // 취소 일시·취소자·예약자·이력 컬럼 추가분 반영
            '.css-1vhoxdy-MuiTableCell-root': { padding: '1px' },
          }}
          size="small"
          aria-label="a dense table">
          <TableHead sx={{ background: '#FAFBFC' }}>
            <TableRow ref={headRowRef} sx={{ height: '40px' }}>
              {isPrivate ? (
                <>
                  {/* 개인레슨 탭 컬럼. 순서를 바꿀 일이 있으면 여기와 아래 본문 셀만 같이 고치면 된다. */}
                  <TableCell align="center" sortDirection={sortBy === 'CLASS_DATE' ? sortDirection.toLowerCase() : false} sx={headCellSx}>
                    <TableSortLabel
                      active={sortBy === 'CLASS_DATE'}
                      direction={sortBy === 'CLASS_DATE' ? sortDirection.toLowerCase() : 'asc'}
                      onClick={() => handleSortClick('CLASS_DATE')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                      레슨 일시
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    {t('Instructor')}
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    {'수강권 명'}
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    {'잔여 횟수'}
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    {'차감 횟수'}
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    {'출결 상태'}
                  </TableCell>
                  {showSignature && (
                    <TableCell align="center" sx={headCellSx}>
                      {'사인'}
                    </TableCell>
                  )}
                  <TableCell align="center" sortDirection={sortBy === 'RESERVED_DATE' ? sortDirection.toLowerCase() : false} sx={headCellSx}>
                    <TableSortLabel
                      active={sortBy === 'RESERVED_DATE'}
                      direction={sortBy === 'RESERVED_DATE' ? sortDirection.toLowerCase() : 'asc'}
                      onClick={() => handleSortClick('RESERVED_DATE')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                      예약 일시
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ ...headCellSx, whiteSpace: 'nowrap' }}>
                    {'예약자'}
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    {'취소 일시'}
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    {'취소자'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '14px', fontWeight: 550 }}>
                    {'예약 취소'}
                  </TableCell>
                  <TableCell align="center" sx={{ ...headCellSx, whiteSpace: 'nowrap' }}>
                    {'이력'}
                  </TableCell>
                </>
              ) : (
                <>
              <TableCell
                align="center"
                sx={{
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 550,
                }}>
                클래스 명
              </TableCell>
              <TableCell
                align="center"
                sortDirection={active && sortBy === 'CLASS_DATE' ? sortDirection.toLowerCase() : false}
                sx={{
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 550,
                }}>
                {active ? (
                  <TableSortLabel
                    active={sortBy === 'CLASS_DATE'}
                    direction={sortBy === 'CLASS_DATE' ? sortDirection.toLowerCase() : 'asc'}
                    onClick={() => handleSortClick('CLASS_DATE')}
                    sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                    레슨 일시
                  </TableSortLabel>
                ) : (
                  '레슨 일시'
                )}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 550,
                }}>
                수강권 명
              </TableCell>
              <TableCell
                align="center"
                sortDirection={active && sortBy === 'RESERVED_DATE' ? sortDirection.toLowerCase() : false}
                sx={{
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 550,
                }}>
                {active ? (
                  <TableSortLabel
                    active={sortBy === 'RESERVED_DATE'}
                    direction={sortBy === 'RESERVED_DATE' ? sortDirection.toLowerCase() : 'asc'}
                    onClick={() => handleSortClick('RESERVED_DATE')}
                    sx={{ '& .MuiTableSortLabel-icon': { opacity: 1 } }}>
                    예약 일시
                  </TableSortLabel>
                ) : (
                  '입장 시간'
                )}
              </TableCell>
              {active && (
                <TableCell
                  align="center"
                  sx={{
                    borderRight: '1px solid #ddd',
                    fontSize: '14px',
                    fontWeight: 550,
                    whiteSpace: 'nowrap',
                  }}>
                  {'예약자'}
                </TableCell>
              )}
              {!active ? (
                <TableCell
                  align="center"
                  sx={{
                    borderRight: '1px solid #ddd',
                    fontSize: '14px',
                    fontWeight: 550,
                  }}>
                  퇴장 시간
                </TableCell>
              ) : (
                <></>
              )}
              <TableCell
                align="center"
                sx={{
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 550,
                }}>
                {t('Instructor')}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 550,
                }}>
                {'강의실'}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 550,
                }}>
                {'출결 상태'}
              </TableCell>
              {active ? (
                <>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 550,
                    }}>
                    {'취소 일시'}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 550,
                    }}>
                    {'취소자'}
                  </TableCell>
                </>
              ) : (
                <></>
              )}
              {/* 새로운 액션 컬럼 추가 */}
              <TableCell align="center" sx={{ fontSize: '14px', fontWeight: 550 }}>
                {'예약 취소'}
              </TableCell>
              {active && (
                <TableCell align="center" sx={{ fontSize: '14px', fontWeight: 550, whiteSpace: 'nowrap' }}>
                  {'이력'}
                </TableCell>
              )}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {lessons &&
              lessons.content.map((lesson, index) => (
                <React.Fragment key={index}>
                <TableRow
                  sx={{
                    height: '40px',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                    ...(isAbsent(lesson) ? { backgroundColor: ABSENT_ROW_BG } : {}),
                  }}>
                  {isPrivate ? (
                    <>
                      {/* 개인레슨 탭 셀. 위 헤더와 같은 순서를 유지할 것. */}
                      <TableCell align="center" component="th" scope="row" sx={bodyCellSx}>
                        {renderBranchBadge(lesson, bigUser?.branch?.branchId)}
                        {formatDate(lesson.classDateTime, 'YYYY.MM.DD (dd) A hh:mm')}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        {lesson.representativeTrainer}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        {lesson.courseTicketName}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        {formatRemainingCount(lesson)}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        {renderDeductionCount(lesson)}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        {t(lesson.status)}
                      </TableCell>
                      {showSignature && (
                      <TableCell align="center" sx={bodyCellSx}>
                        {lesson.signatureImage ? (
                          <Box
                            component="img"
                            src={lesson.signatureImage}
                            alt="회원 사인"
                            title="클릭하면 크게 보기"
                            onClick={() => setSignaturePreview(lesson.signatureImage)}
                            sx={{
                              height: '44px',
                              maxWidth: '68px',
                              objectFit: 'contain',
                              cursor: 'pointer',
                              verticalAlign: 'middle',
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      )}
                      <TableCell align="center" sx={bodyCellSx}>
                        {formatDate(lesson.reservedDateTime, 'YYYY.MM.DD (dd) A hh:mm')}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        {formatActor(lesson.registeredByName, lesson.registeredByType)}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        {lesson.canceledDateTime ? formatDate(lesson.canceledDateTime, 'YYYY.MM.DD (dd) A hh:mm') : '-'}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        {formatCanceledBy(lesson)}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          onClick={() => handleActionClick(lesson)}
                          variant="contained"
                          size="small"
                          disabled={lesson.status === 'CANCELED'}
                          sx={{
                            backgroundColor: lesson.status === 'CANCELED' ? '#808080' : '#d32f2f',
                            color: '#ffffff',
                            borderRadius: '8px',
                            fontSize: '14px',
                            minWidth: 'auto',
                            padding: '4px 8px',
                            '&:hover': {
                              backgroundColor: lesson.status === 'CANCELED' ? '#808080' : '#c62828',
                            },
                            '&:disabled': {
                              backgroundColor: '#808080',
                              color: '#ffffff',
                            },
                          }}>
                          {lesson.status === 'CANCELED' ? '취소' : '취소'}
                        </Button>
                      </TableCell>
                      <TableCell align="center" sx={{ px: 0 }}>
                        <ReservationHistoryToggle
                          expanded={expandedId === lesson.reservationId}
                          onToggle={() => setExpandedId(prev => (prev === lesson.reservationId ? null : lesson.reservationId))}
                        />
                      </TableCell>
                    </>
                  ) : (
                    <>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                    {isOtherBranchLesson(lesson, bigUser?.branch?.branchId) && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          marginRight: '4px',
                          padding: '0 5px',
                          borderRadius: '10px',
                          border: '1px solid #E0E4EA',
                          backgroundColor: '#F5F7FA',
                          color: '#5C6470',
                          fontSize: '11px',
                          fontWeight: 600,
                          lineHeight: '16px',
                          verticalAlign: 'middle',
                        }}>
                        {lesson.branchName}
                      </Box>
                    )}
                    {canOpenGroupSchedule(lesson, bigUser?.branch?.branchId) ? (
                      <Box
                        component="span"
                        role="link"
                        tabIndex={0}
                        onClick={() => handleClassNameClick(lesson)}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleClassNameClick(lesson);
                          }
                        }}
                        title="그룹 스케줄에서 보기"
                        sx={{
                          color: '#1976d2',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          '&:hover': { color: '#115293' },
                        }}>
                        {lesson.className}
                      </Box>
                    ) : (
                      lesson.className
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                    {formatDate(lesson.classDateTime, 'YYYY.MM.DD (dd) A hh:mm')}
                  </TableCell>
                  <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                    {lesson.courseTicketName}
                  </TableCell>
                  {active ? (
                    <>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {formatDate(lesson.reservedDateTime, 'YYYY.MM.DD (dd) A hh:mm')}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {formatActor(lesson.registeredByName, lesson.registeredByType)}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {lesson.representativeTrainer}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {lesson.exerciseRoomName}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {t(lesson.status)}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {lesson.canceledDateTime ? formatDate(lesson.canceledDateTime, 'YYYY.MM.DD (dd) A hh:mm') : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {formatCanceledBy(lesson)}
                      </TableCell>
                      {/* 수정된 액션 버튼 */}
                      <TableCell align="center">
                        <Button
                          onClick={() => handleActionClick(lesson)}
                          variant="contained"
                          size="small"
                          disabled={lesson.status === 'CANCELED'}
                          sx={{
                            backgroundColor: lesson.status === 'CANCELED' ? '#808080' : '#d32f2f',
                            color: '#ffffff',
                            borderRadius: '8px',
                            fontSize: '14px',
                            minWidth: 'auto',
                            padding: '4px 8px',
                            '&:hover': {
                              backgroundColor: lesson.status === 'CANCELED' ? '#808080' : '#c62828',
                            },
                            '&:disabled': {
                              backgroundColor: '#808080',
                              color: '#ffffff',
                            },
                          }}>
                          {lesson.status === 'CANCELED' ? '취소' : '취소'}
                        </Button>
                      </TableCell>
                      <TableCell align="center" sx={{ px: 0 }}>
                        <ReservationHistoryToggle
                          expanded={expandedId === lesson.reservationId}
                          onToggle={() => setExpandedId(prev => (prev === lesson.reservationId ? null : lesson.reservationId))}
                        />
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {lesson.entryDateTime ? formatDate(lesson.entryDateTime, 'YYYY.MM.DD (dd) A hh:mm') : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {lesson.exitDateTime ? formatDate(lesson.exitDateTime, 'YYYY.MM.DD (dd) A hh:mm') : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {lesson.representativeTrainer}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontSize: '13px' }}>
                        {t(lesson.status)}
                      </TableCell>
                    </>
                  )}
                    </>
                  )}
                </TableRow>
                {expandedId === lesson.reservationId && (
                  <TableRow>
                    <TableCell colSpan={headRowRef.current?.children.length || 1} sx={{ backgroundColor: '#FAFBFD', pl: '24px' }}>
                      <ReservationEventTimeline memberId={user.memberId} reservationId={lesson.reservationId} token={bigUser.token} />
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 취소 확인 모달 — 공용 ConfirmModal 사용 ("취소" 키워드를 빨강으로 강조). */}
      <ConfirmModal
        open={cancelDialog.open}
        onClose={handleCancelDialogClose}
        onConfirm={handleCancelConfirm}
        message="정말로 예약을 취소하시겠습니까?"
        highlightText="취소"
        cancelText="닫기"
        confirmText="확인"
      />

      {/* 사인 확대 — S3 공개 URL 이라 별도 인증 없이 그대로 뜬다. */}
      <Dialog open={!!signaturePreview} onClose={() => setSignaturePreview(null)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', backgroundColor: '#fff' }}>
          {signaturePreview && (
            <Box
              component="img"
              src={signaturePreview}
              alt="회원 사인"
              sx={{ width: '100%', maxHeight: '60vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>

      <CommonPagination
        totalElements={lessons?.totalElements}
        limit={lessons?.size}
        page={query.page}
        onChange={handleChangePage}
      />
    </>
  );
}
